import type { Apunte } from '../api/accounting';
import type { Comision } from '../api/commissions';
import { datosLibro } from './libro';
import { crearXlsx, type Celda } from './xlsx';

// El libro del año en Excel para la gestoría: una hoja con cada apunte y otra
// con el resumen por trimestre. Los mismos datos que el CSV (libro.ts), pero con
// las cifras como números: la gestoría puede sumar y filtrar sin convertir nada.

const r2 = (n: number) => Math.round(n * 100) / 100;

export function construirLibroXlsx(anio: string, apuntes: Apunte[], comisiones: Comision[]): Uint8Array {
  const { filas, trimestres, hayRetenciones } = datosLibro(anio, apuntes, comisiones);

  const libro: Celda[][] = [
    ['Fecha', 'Trimestre', 'Tipo', 'Categoría', 'Concepto', 'Contraparte',
      'Base imponible', 'IVA %', 'IVA €', 'Retención IRPF %', 'Retención IRPF €', 'Total'],
    ...filas.map((a): Celda[] => [
      a.fecha ? { fecha: a.fecha } : null,
      a.trimestre || null,
      a.tipo === 'ingreso' ? 'Ingreso' : 'Gasto',
      a.categoria || null,
      a.concepto || null,
      a.contraparte || null,
      r2(a.base_imponible ?? 0),
      a.iva_pct ?? null,
      r2(a.iva_amount ?? 0),
      a.irpf_pct || null,
      a.irpf_amount ? r2(a.irpf_amount) : null,
      r2(a.total ?? 0),
    ]),
  ];

  const resumen: Celda[][] = [
    ['Trimestre', 'Ingresos', 'Gastos', 'Resultado',
      ...(hayRetenciones ? ['Retenciones IRPF (Modelo 111)'] : [])],
    ...trimestres.map((t): Celda[] => {
      const total = t.t === 'TOTAL AÑO';
      const v = (n: number): Celda => (total ? { negrita: r2(n) } : r2(n));
      return [total ? { negrita: t.t } : `${anio}-${t.t}`, v(t.ingresos), v(t.gastos), v(t.ingresos - t.gastos),
        ...(hayRetenciones ? [v(t.retenido)] : [])];
    }),
    [],
    ['Ingresos y gastos cuentan base + IVA. En una factura con retención de IRPF, es el total más lo retenido.'],
  ];

  return crearXlsx([
    { nombre: `Libro ${anio}`, anchos: [12, 11, 9, 20, 44, 32, 15, 8, 12, 17, 17, 13], filas: libro, decimales: [6, 8, 10, 11] },
    { nombre: 'Resumen', anchos: [14, 14, 14, 14, 30], filas: resumen, decimales: [1, 2, 3, 4] },
  ]);
}

export function descargarLibroXlsx(anio: string, apuntes: Apunte[], comisiones: Comision[]) {
  const datos = construirLibroXlsx(anio, apuntes, comisiones);
  // `datos` ocupa su buffer entero (lo crea crearXlsx), así que se pasa el buffer.
  const blob = new Blob([datos.buffer as ArrayBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `libro-habitania-${anio}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
